import { useRef, useState, useCallback } from "react";

// WebRTC peer-connection concern for 1-on-1 DM calls, extracted from CallContext.
// Owns the RTCPeerConnection, the local media stream, the media DOM refs
// (audio + local/remote video) and the mute/camera controls. Exposes high-level
// signalling operations (startOffer / answerOffer / applyAnswer / addIce) so the
// call state machine in CallContext stays readable.
//
// Params are all stable (refs, memoised callbacks) so the returned callbacks are
// stable and safe to list in the signalling effect's dependency array.
export function usePeerConnection({ iceServers, send, callIdRef, mediaRef, phaseRef, onRemoteEnd }) {
  const pcRef = useRef(null);
  const localRef = useRef(null);
  const audioElRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);

  const attachRemote = useCallback((stream) => {
    if (mediaRef.current === "video" && remoteVideoRef.current) remoteVideoRef.current.srcObject = stream;
    else if (audioElRef.current) audioElRef.current.srcObject = stream;
  }, [mediaRef]);

  const newPc = useCallback((toId) => {
    const pc = new RTCPeerConnection({ iceServers: iceServers && iceServers.length ? iceServers : [{ urls: "stun:stun.l.google.com:19302" }] });
    pc.onicecandidate = (e) => { if (e.candidate) send({ type: "call_signal", to: toId, call_id: callIdRef.current, data: { kind: "ice", candidate: e.candidate } }); };
    pc.ontrack = (e) => attachRemote(e.streams[0]);
    pc.onconnectionstatechange = () => {
      if (["failed", "disconnected", "closed"].includes(pc.connectionState) && phaseRef.current === "in-call") onRemoteEnd();
    };
    pcRef.current = pc;
    return pc;
  }, [iceServers, send, callIdRef, phaseRef, attachRemote, onRemoteEnd]);

  const getMic = useCallback(async (kind) => {
    const wantVideo = kind === "video";
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: wantVideo });
    localRef.current = stream;
    if (wantVideo && localVideoRef.current) localVideoRef.current.srcObject = stream;
    return stream;
  }, []);

  const addLocalTracks = (pc) => { localRef.current?.getTracks().forEach((tr) => pc.addTrack(tr, localRef.current)); };

  // caller: peer accepted → create + send the offer
  const startOffer = useCallback(async (toId) => {
    const pc = newPc(toId);
    addLocalTracks(pc);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    send({ type: "call_signal", to: toId, call_id: callIdRef.current, data: { kind: "offer", sdp: offer } });
  }, [newPc, send, callIdRef]);

  // answerer: got an offer → create + send the answer
  const answerOffer = useCallback(async (toId, sdp) => {
    const pc = newPc(toId);
    addLocalTracks(pc);
    await pc.setRemoteDescription(new RTCSessionDescription(sdp));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    send({ type: "call_signal", to: toId, call_id: callIdRef.current, data: { kind: "answer", sdp: answer } });
  }, [newPc, send, callIdRef]);

  const applyAnswer = useCallback(async (sdp) => {
    if (pcRef.current) await pcRef.current.setRemoteDescription(new RTCSessionDescription(sdp));
  }, []);

  const addIce = useCallback(async (candidate) => {
    if (pcRef.current) { try { await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate)); } catch (e) { /* ignore late/duplicate ICE */ } }
  }, []);

  const toggleMute = useCallback(() => {
    const t = localRef.current?.getAudioTracks()[0];
    if (t) { t.enabled = !t.enabled; setMuted(!t.enabled); }
  }, []);

  const toggleCamera = useCallback(() => {
    const t = localRef.current?.getVideoTracks()[0];
    if (t) { t.enabled = !t.enabled; setCamOff(!t.enabled); }
  }, []);

  // Close the peer connection and stop the local stream (no DOM cleanup).
  // Used when moving to the voicemail state after an unanswered call.
  const stopLocal = useCallback(() => {
    if (pcRef.current) { try { pcRef.current.close(); } catch (e) {} pcRef.current = null; }
    if (localRef.current) { localRef.current.getTracks().forEach((t) => t.stop()); localRef.current = null; }
  }, []);

  // Full teardown: stop the peer + local stream, detach media elements, reset controls.
  const closePeer = useCallback(() => {
    stopLocal();
    if (audioElRef.current) audioElRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    setMuted(false); setCamOff(false);
  }, [stopLocal]);

  return {
    pcRef, localRef, audioElRef, localVideoRef, remoteVideoRef,
    muted, camOff, getMic, startOffer, answerOffer, applyAnswer, addIce,
    toggleMute, toggleCamera, stopLocal, closePeer,
  };
}
